'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { earnSunsetPhaseBunja } from '@flipster/features/src/flipster-earn/sunset/phase-bunja';
import { FormattedMessage } from '@flipster/i18n';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { Button } from '@flipster/react/design-system/button';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import copyTradingCn from '../assets/copy-trading-cn.webp';
import copyTradingEn from '../assets/copy-trading-en.webp';
import copyTradingJp from '../assets/copy-trading-en.webp';
import copyTradingRu from '../assets/copy-trading-ru.webp';
import copyTradingTw from '../assets/copy-trading-tw.webp';
import copyTradingVi from '../assets/copy-trading-vi.webp';
import Earn from '../assets/img-earn.webp';

const onClickLearnMore = (title: string, index: number) => {
  segment.track(MainEvent.IntroduceAdvantageLearnMoreClick, {
    section_title: title,
    list_order: index,
    event_location: 'main_page_view',
  });
};

const UniqueSettlingPoint = () => {
  const t = useTranslations();
  const locale = useSupportedLocale();
  const { isFlipsterEarnHiddenAtom } = useBunja(earnSunsetPhaseBunja);
  const isFlipsterEarnHidden = useAtomValue(isFlipsterEarnHiddenAtom);

  const contents = [
    {
      id: 'copy-trading',
      title: t('Home.r251222.Trade_like_pros'),
      description: t('Home.r251222.Copy_master_traders_strategies_real_time'),
      image: copyTradingImages[locale],
      imageWidthCss: css({ maxW: 590 }),
      width: 590,
      pointList: [
        t('Home.r251222.Automate_entries_exits_when_master_traders_move'),
        t('Home.r251222.Transparent_performance'),
        t('Home.r251222.Track_pnl_roi_win_rate_trade_history'),
      ],
      learnMoreLink: '/copy-trading',
    },
    {
      id: 'earn_and_trade_simultaneously',
      title: t('Main.r251104.Earn_and_trade_simultaneously'),
      description: t('Main.r251104.Trade_actively_earn_passively_with_same_funds_perpetual_margin'),
      image: Earn.src,
      imageWidthCss: css({ maxW: 528 }),
      width: 528,
      pointList: [
        t('Main.r251104.Funds_remain_fully_liquid_while_trade'),
        t('Main.r251104.Apr_applies_automatically_while_trading'),
        t('Main.r251104.Earn_up_to_percent_apr_supported_assets', { percent: '6%' }),
      ],
      learnMoreLink: '/earn',
    },
  ];

  const visibleContents = isFlipsterEarnHidden
    ? contents.filter((content) => content.id !== 'earn_and_trade_simultaneously')
    : contents;

  return (
    <section className={css({ py: { base: 48, tablet: 64 } })}>
      {visibleContents.map((content, index) => (
        <div
          key={content.id}
          className={flex({
            align: 'center',
            justify: 'space-between',
            w: 'auto',
            maxW: { tablet: 'desktopMaxWidth' },
            mx: { base: 16, smallDesktop: 'auto' },
            _odd: { flexDir: { base: 'column', tablet: 'row-reverse' } },
            _even: { mt: { base: 64, tablet: 96 }, flexDir: { base: 'column', tablet: 'row' } },
            gap: 20,
          })}
        >
          <div className={content.imageWidthCss}>
            <img
              src={content.image}
              alt={content.id}
              width={content.width}
              className={css({ rounded: { base: 8, tablet: 16 }, w: '100%', h: 'auto' })}
            />
          </div>
          <div
            className={css({ maxW: 590, pt: { base: 24, tablet: 0 }, textAlign: { base: 'center', tablet: 'left' } })}
          >
            <h2
              className={css({
                textStyle: 'Display/XL',
                color: 'content.default.level-5',
                whiteSpace: 'pre-line',
              })}
            >
              {content.title}
            </h2>
            <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-3', pt: 16 })}>
              {content.description}
            </p>
            <ul className={css({ pt: 26, display: { base: 'none', tablet: 'block' } })}>
              {content.pointList.map((point) => (
                <li key={point} className={flex({ align: 'center', gap: 8, py: 6 })}>
                  <CheckCircleIcon color={token('colors.content.accent.info')} size={24} />
                  <span className={css({ color: 'content.default.level-3', textStyle: 'Text/L/Regular' })}>
                    {point}
                  </span>
                </li>
              ))}
            </ul>
            <Button
              as='a'
              size='large'
              label={<FormattedMessage id='Common.r250401.Learn_more' />}
              css={{ minW: 120, mt: 48 }}
              href={content.learnMoreLink}
              onClick={() => onClickLearnMore(content.title, index)}
            />
          </div>
        </div>
      ))}
    </section>
  );
};

export default UniqueSettlingPoint;

const copyTradingImages = {
  en: copyTradingEn.src,
  ['zh-CN']: copyTradingCn.src,
  ['zh-TW']: copyTradingTw.src,
  ru: copyTradingRu.src,
  vi: copyTradingVi.src,
  ja: copyTradingJp.src,
  ['en-AE']: copyTradingEn.src,
};
